// @givmo/board — the year on Conflicts of interest (CBG-YR2-WEB2; frame d1-year-card): the
// card for the year waiting on the person, and their own annual disclosures in the board's records.
//
// The card names who is asking and when it is due, says whether last year's answers come back filled
// in, and offers Start (Continue while answers wait unsent in this tab). Once the person has answered
// an open year it says so, with their copy and their changes as PDFs. A year past its due date says
// "It was due …. You can still send it." and nothing louder: no red, no countdown, no count.

import { Link as RouterLink, useNavigate } from '../../board-routing';
import type { BoardCoiDirectorCycleOut, BoardCoiEntryOut } from '@givmo/api-client';
import { Button, Card, CardBody, toast } from '@givmo/design-system';
import { BOARD_DIRECTOR_PAGES } from '../../board-routes';
import { useBoardDirectorOperations } from '../../board-runtime';
import { directorBoardSearch } from '../../director/director-board';
import { displayDate } from '../../lib/display';
import type { ConflictDisclosureRow } from '../conflicts/conflicts-decode';
import { isPastDue, longDay } from '../conflicts/coi-decode';
import { pdfName, readerTimeZone, savePdf } from '../conflicts/coi-download';
import { EntryLine } from '../conflicts/coi-parts';
import { coiWords as w } from '../conflicts/coi-words';
import { useOwnCoiResponse } from './director-coi';

const TITLE = 'font-sans text-s18 font-w700 tracking-givmo text-text-strong';
const BODY = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const LINK =
  'font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none hover:text-brand-700 focus-visible:underline';

/**
 * The year the card is about: the newest one still taking this person's first answers, or the newest
 * open one they've answered. Any other year has no card (its record is in the list below).
 */
export function yearForCard(
  cycles: readonly BoardCoiDirectorCycleOut[],
): BoardCoiDirectorCycleOut | null {
  return (
    cycles.find(
      (cycle) =>
        (cycle.response == null && cycle.accepts_answers) ||
        (cycle.response != null && cycle.status === 'open'),
    ) ?? null
  );
}

/** Download one of the person's own PDFs for a year (D6). */
export function useOwnPdf(orgRef: string | null) {
  const operations = useBoardDirectorOperations();
  return async (cycle: { cycle_id: string; label: string }, view: 'clean' | 'changes') => {
    try {
      const { data } = await operations.directorGetOwnBoardCoiResponseDocument({
        path: { org_ref: orgRef ?? '', cycle_id: cycle.cycle_id },
        query: { view, time_zone: readerTimeZone() },
        throwOnError: true,
      });
      savePdf(
        data,
        pdfName(cycle.label, view === 'changes' ? 'changes' : 'conflicts of interest form'),
      );
    } catch {
      toast.error(w.couldNotDownload);
    }
  };
}

export function YearCard({
  orgRef,
  cycle,
  organization,
  kept,
  canSign,
}: {
  orgRef: string;
  cycle: BoardCoiDirectorCycleOut;
  organization: string;
  /** Answers wait unsent in this tab for this year: "Continue". */
  kept: boolean;
  /** The account can sign here (not one another organization's sign-in lock binds). */
  canSign: boolean;
}) {
  const download = useOwnPdf(orgRef);
  const navigate = useNavigate();
  const search = { ...directorBoardSearch(orgRef), cycle: cycle.cycle_id };
  const answered = cycle.response ?? null;
  const due = longDay(cycle.due_on);
  // An answered year's changes are named by the year they compare with, which only the response
  // itself says (D4's `previous`), never a guess from this year's label.
  const responseQuery = useOwnCoiResponse(orgRef, cycle.cycle_id, answered !== null);
  const previousLabel = responseQuery.data?.previous?.cycle_label ?? null;

  return (
    <Card testId="director-coi-year">
      <CardBody className="flex flex-col gap-10">
        <h2 className={TITLE}>{w.yourYear(cycle.label)}</h2>
        {answered === null ? (
          <>
            <p className={BODY} data-testid="director-coi-year-ask">
              {cycle.joined_after_close === true
                ? w.yearAskJoinedAfterClose(organization, cycle.label)
                : isPastDue(cycle.due_on)
                  ? w.yearAskWasDue(organization, due)
                  : w.yearAsk(organization, due)}
            </p>
            {cycle.has_previous ? <p className={BODY}>{w.filledIn}</p> : null}
            {canSign ? (
              <div>
                <Button
                  appearance="saas"
                  tone="primary"
                  onClick={() =>
                    void navigate({ to: BOARD_DIRECTOR_PAGES.annualDisclosure, search })
                  }
                  testId="director-coi-year-start"
                >
                  {kept ? w.continue : w.start}
                </Button>
              </div>
            ) : null}
          </>
        ) : (
          <>
            <p className={BODY} data-testid="director-coi-year-answered">
              {answered.road === 'paper'
                ? w.recordedOn(displayDate(answered.submitted_at))
                : w.signedOn(displayDate(answered.submitted_at))}
            </p>
            <div className="flex flex-wrap gap-10">
              <Button
                appearance="saas"
                tone="secondary"
                onClick={() => void download(cycle, 'clean')}
                testId="director-coi-year-copy"
              >
                {w.yourCopy}
              </Button>
              {previousLabel !== null ? (
                <Button
                  appearance="saas"
                  tone="secondary"
                  onClick={() => void download(cycle, 'changes')}
                  testId="director-coi-year-changes"
                >
                  {w.yourChanges(previousLabel)}
                </Button>
              ) : null}
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * One of the person's own annual disclosures in the board's records (d1-year-card's "Annual
 * disclosure, 2026"): the year, when it was signed or recorded, the interests its answers name with
 * their notes (the person's own response read, D4), and "Your answers".
 */
export function OwnAnnualCard({ orgRef, entry }: { orgRef: string; entry: ConflictDisclosureRow }) {
  const annual = entry.annual;
  // The notes beside each interest are in the response itself, read at the head of its corrections:
  // only the head's own card shows them (an earlier, corrected one lists its names alone).
  const responseQuery = useOwnCoiResponse(orgRef, annual?.cycle_id ?? null, annual !== null);
  if (annual === null) return null;
  const response = responseQuery.data ?? null;
  const fromResponse = response !== null && response.record_id === entry.record_id;
  const paper = entry.capture_method === 'manual_on_behalf';
  const on = displayDate(entry.disclosed_on);
  const entries: BoardCoiEntryOut[] = fromResponse
    ? response.answers.flatMap((answer) => answer.entries ?? [])
    : (annual.interests ?? []).map((interest) => ({
        key: interest.entry_key,
        name: interest.name ?? null,
        person: interest.person ?? null,
        whose: interest.whose ?? null,
        what: interest.what ?? null,
        position: interest.position ?? null,
        relationship: interest.relationship ?? null,
      }));

  return (
    <Card testId={`director-coi-own-${entry.record_id}`}>
      <CardBody className="flex flex-col gap-10">
        <div className="flex flex-wrap items-baseline justify-between gap-8">
          <span className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
            {w.annualOwnTitle(annual.cycle_label)}
          </span>
          <span className="font-sans text-s13 font-w400 tracking-givmo text-text-tertiary">
            {paper ? w.recordedOn(on) : w.signedOn(on)}
          </span>
        </div>
        {entries.length === 0 ? (
          <span className="font-sans text-s14 font-w400 tracking-givmo text-text-strong">
            {w.namesNoInterest}
          </span>
        ) : (
          <div className="flex flex-col gap-8">
            {entries.map((item) => (
              <EntryLine
                key={item.key}
                entry={item}
                testId={`director-coi-own-entry-${item.key}`}
              />
            ))}
          </div>
        )}
        {entry.status === 'superseded' ? (
          <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
            {w.replacedByLaterForm}
          </span>
        ) : null}
        <RouterLink
          to={BOARD_DIRECTOR_PAGES.annualDisclosure}
          search={{ ...directorBoardSearch(orgRef), cycle: annual.cycle_id, view: 'answers' }}
          className={`${LINK} self-start`}
          data-testid={`director-coi-own-answers-${entry.record_id}`}
        >
          {w.yourAnswers}
        </RouterLink>
      </CardBody>
    </Card>
  );
}
